/* ═══════════════════════════════════════════
   READER READING RÉSUMÉ — PDF Generator
   Uses jsPDF for client-side A4 PDF creation.
   Teal & slate Narratoria branding.
═══════════════════════════════════════════ */
import { jsPDF } from 'jspdf';

// Brand colours
const TEAL = [13, 148, 136];      // #0D9488
const TEAL_LIGHT = [204, 251, 241]; // #CCFBF1
const SLATE_900 = [15, 23, 42];
const SLATE_700 = [51, 65, 85];
const SLATE_500 = [100, 116, 139];
const SLATE_300 = [203, 213, 225];
const WHITE = [255, 255, 255];

function setColor(pdf, rgb) {
  pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
}

function drawLine(pdf, y, width) {
  pdf.setDrawColor(SLATE_300[0], SLATE_300[1], SLATE_300[2]);
  pdf.setLineWidth(0.3);
  pdf.line(20, y, width - 20, y);
}

function addPage(pdf, pageNum) {
  pdf.addPage();
  // Footer on previous page
  return pageNum + 1;
}

function drawFooter(pdf, verificationCode, pageNum, totalPages) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const y = pageHeight - 15;

  drawLine(pdf, y - 5, pageWidth);

  pdf.setFontSize(7);
  setColor(pdf, SLATE_500);
  pdf.text('Verified ARC Reader — Narratoria Ltd', 20, y);
  pdf.text(`Generated ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, y, { align: 'center' });
  pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 20, y, { align: 'right' });

  if (verificationCode) {
    pdf.setFontSize(6);
    pdf.text(`Verification: ${verificationCode}`, 20, y + 4);
  }
}

function sectionHeader(pdf, title, y) {
  pdf.setFillColor(TEAL_LIGHT[0], TEAL_LIGHT[1], TEAL_LIGHT[2]);
  pdf.roundedRect(20, y - 1, pdf.internal.pageSize.getWidth() - 40, 8, 2, 2, 'F');
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  setColor(pdf, TEAL);
  pdf.text(title, 24, y + 5);
  return y + 14;
}

function statBox(pdf, x, y, label, value, width = 35) {
  pdf.setFillColor(248, 250, 252); // slate-50
  pdf.roundedRect(x, y, width, 18, 2, 2, 'F');
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  setColor(pdf, SLATE_900);
  pdf.text(String(value), x + width / 2, y + 8, { align: 'center' });
  pdf.setFontSize(6);
  pdf.setFont('helvetica', 'normal');
  setColor(pdf, SLATE_500);
  pdf.text(label, x + width / 2, y + 14, { align: 'center' });
}

/**
 * Generate a Reading Résumé PDF
 * @param {Object} data - All résumé data
 * @returns {jsPDF} - The PDF document
 */
export function generateResumePdf(data) {
  const {
    readerName,
    memberSince,
    readingHistory = [],
    reviews = [],
    arcRequests = [],
    redemptions = [],
    totalPoints = 0,
    socialMedia = {},
    preferences = {},
    platforms = [],
    resumeBio = '',
    verificationCode = '',
  } = data;

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth(); // 210mm
  let y = 0;

  // ═══════════════════════════════════════
  // PAGE 1 — Header + Stats + ARC History
  // ═══════════════════════════════════════

  // Header banner
  pdf.setFillColor(TEAL[0], TEAL[1], TEAL[2]);
  pdf.rect(0, 0, pageWidth, 40, 'F');

  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  setColor(pdf, WHITE);
  pdf.text('Reading Résumé', 20, 18);

  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.text(readerName || 'Reader', 20, 28);

  pdf.setFontSize(8);
  setColor(pdf, [204, 251, 241]); // teal-100
  pdf.text(`Narratoria Reader Community · Member since ${memberSince || 'N/A'}`, 20, 35);

  y = 50;

  // ─── Reading Stats ───
  y = sectionHeader(pdf, 'Reading Stats', y);

  const readBooks = readingHistory.filter(b => b.readStatus === 'read');
  const currentYear = new Date().getFullYear();
  const booksThisYear = readBooks.filter(b => b.dateRead && b.dateRead.includes(String(currentYear))).length;
  const ratedBooks = readingHistory.filter(b => b.rating > 0);
  const avgRating = ratedBooks.length > 0
    ? (ratedBooks.reduce((s, b) => s + b.rating, 0) / ratedBooks.length).toFixed(1)
    : '—';

  // Calculate books per month
  const monthsActive = (() => {
    const dates = readBooks.map(b => b.dateRead).filter(Boolean).map(d => new Date(d)).filter(d => !isNaN(d));
    if (dates.length < 2) return 1;
    const earliest = new Date(Math.min(...dates));
    const latest = new Date(Math.max(...dates));
    return Math.max(1, Math.round((latest - earliest) / (30 * 24 * 60 * 60 * 1000)));
  })();
  const booksPerMonth = (readBooks.length / monthsActive).toFixed(1);

  // Stats row
  const statWidth = (pageWidth - 50) / 5;
  statBox(pdf, 20, y, 'Books Read', readBooks.length, statWidth);
  statBox(pdf, 20 + statWidth + 2, y, 'This Year', booksThisYear, statWidth);
  statBox(pdf, 20 + (statWidth + 2) * 2, y, 'Avg Rating', avgRating, statWidth);
  statBox(pdf, 20 + (statWidth + 2) * 3, y, 'Books/Month', booksPerMonth, statWidth);
  statBox(pdf, 20 + (statWidth + 2) * 4, y, 'Total Points', totalPoints, statWidth);
  y += 24;

  // Favourite genres (top 3 from tagged books)
  const genreCounts = {};
  for (const book of readingHistory) {
    const bookGenres = book.autoTags?.genres || {};
    for (const g of Object.keys(bookGenres)) {
      genreCounts[g] = (genreCounts[g] || 0) + 1;
    }
  }
  const topGenres = Object.entries(genreCounts).sort(([, a], [, b]) => b - a).slice(0, 3).map(([g]) => g);

  // Favourite tropes (top 5)
  const tropeCounts = {};
  for (const book of readingHistory) {
    const bookTropes = book.autoTags?.tropes || {};
    for (const t of Object.keys(bookTropes)) {
      tropeCounts[t] = (tropeCounts[t] || 0) + 1;
    }
  }
  const topTropes = Object.entries(tropeCounts).sort(([, a], [, b]) => b - a).slice(0, 5).map(([t]) => t);

  // Steam level
  const steamCounts = {};
  for (const book of readingHistory) {
    const bookSteam = book.autoTags?.steamLevel || {};
    for (const s of Object.keys(bookSteam)) {
      steamCounts[s] = (steamCounts[s] || 0) + 1;
    }
  }
  const topSteam = Object.entries(steamCounts).sort(([, a], [, b]) => b - a)[0]?.[0];

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  setColor(pdf, SLATE_700);

  if (topGenres.length > 0) {
    pdf.text('Favourite Genres:', 20, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(topGenres.join('  ·  '), 58, y);
    y += 6;
  }

  if (topTropes.length > 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Favourite Tropes:', 20, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(topTropes.join('  ·  '), 58, y);
    y += 6;
  }

  if (topSteam) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Preferred Steam:', 20, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(topSteam, 58, y);
    y += 6;
  }

  if (platforms.length > 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Verified via:', 20, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(platforms.join(', '), 58, y);
    y += 6;
  }

  y += 4;

  // ─── ARC History ───
  y = sectionHeader(pdf, 'ARC History', y);

  const completedArcs = arcRequests.filter(r => r.status === 'approved');
  const arcReviews = reviews.filter(r => r.rating > 0);
  const arcAvgRating = arcReviews.length > 0
    ? (arcReviews.reduce((s, r) => s + r.rating, 0) / arcReviews.length).toFixed(1)
    : '—';
  const reviewCompletion = completedArcs.length > 0
    ? Math.round((arcReviews.length / completedArcs.length) * 100)
    : 0;

  // Review platforms from socialMedia
  const reviewPlatforms = [];
  if (socialMedia.amazon) reviewPlatforms.push('Amazon');
  if (socialMedia.goodreads) reviewPlatforms.push('Goodreads');
  if (socialMedia.instagram) reviewPlatforms.push('Instagram');
  if (socialMedia.tiktok) reviewPlatforms.push('TikTok');
  if (socialMedia.other) reviewPlatforms.push(socialMedia.other);

  const arcStatWidth = (pageWidth - 46) / 4;
  statBox(pdf, 20, y, 'ARCs Completed', completedArcs.length, arcStatWidth);
  statBox(pdf, 20 + arcStatWidth + 2, y, 'Avg ARC Rating', arcAvgRating, arcStatWidth);
  statBox(pdf, 20 + (arcStatWidth + 2) * 2, y, 'Review Rate', `${reviewCompletion}%`, arcStatWidth);
  statBox(pdf, 20 + (arcStatWidth + 2) * 3, y, 'Reviews Posted', arcReviews.length, arcStatWidth);
  y += 24;

  if (reviewPlatforms.length > 0) {
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    setColor(pdf, SLATE_700);
    pdf.text('Review Platforms:', 20, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(reviewPlatforms.join(', '), 58, y);
    y += 6;
  }

  // Rewards earned
  if (redemptions.length > 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Rewards Earned:', 20, y);
    pdf.setFont('helvetica', 'normal');
    const rewardNames = [...new Set(redemptions.map(r => r.rewardName))].slice(0, 3);
    pdf.text(rewardNames.join(', '), 58, y);
    y += 6;
  }

  y += 4;

  // ─── Top Rated Books ───
  y = sectionHeader(pdf, 'Top Rated Books', y);

  const fiveStars = readingHistory
    .filter(b => b.rating === 5)
    .slice(0, 10);

  if (fiveStars.length > 0) {
    pdf.setFontSize(8);
    for (const book of fiveStars) {
      if (y > 255) { addPage(pdf, 1); y = 20; }
      pdf.setFont('helvetica', 'bold');
      setColor(pdf, SLATE_900);
      const stars = '★★★★★';
      pdf.text(stars, 20, y);
      pdf.text(book.title || '', 36, y);
      pdf.setFont('helvetica', 'normal');
      setColor(pdf, SLATE_500);
      const authorText = book.author ? ` — ${book.author}` : '';
      pdf.text(authorText, 36 + pdf.getTextWidth(book.title || '') + 1, y);
      y += 5;
    }
  } else {
    pdf.setFontSize(8);
    setColor(pdf, SLATE_500);
    pdf.text('No 5-star ratings recorded yet.', 20, y);
    y += 6;
  }

  // Taste snapshot
  if (topGenres.length > 0 || topTropes.length > 0) {
    y += 4;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    setColor(pdf, TEAL);
    const tasteItems = [...topGenres.slice(0, 2), ...topTropes.slice(0, 3)];
    pdf.text(`Taste snapshot: ${tasteItems.join(' · ')}`, 20, y);
    y += 8;
  }

  // ─── Community Contribution ───
  if (y > 240) { addPage(pdf, 1); y = 20; }
  y = sectionHeader(pdf, 'Community Contribution', y);

  const contribStatWidth = (pageWidth - 46) / 3;
  statBox(pdf, 20, y, 'Books Tagged', totalPoints > 0 ? Math.floor(totalPoints / 5) : 0, contribStatWidth);
  statBox(pdf, 20 + contribStatWidth + 2, y, 'Books Imported', readingHistory.length, contribStatWidth);
  statBox(pdf, 20 + (contribStatWidth + 2) * 2, y, 'Points Earned', totalPoints, contribStatWidth);
  y += 24;

  // ═══════════════════════════════════════
  // BIO / ABOUT SECTION
  // ═══════════════════════════════════════

  if (resumeBio || (preferences.favoriteGenres?.length > 0) || preferences.favoriteTropes) {
    if (y > 240) { addPage(pdf, 1); y = 20; }
    y = sectionHeader(pdf, 'About Me', y);

    if (resumeBio) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      setColor(pdf, SLATE_700);
      const bioLines = pdf.splitTextToSize(resumeBio, pageWidth - 40);
      pdf.text(bioLines, 20, y);
      y += bioLines.length * 4.5 + 4;
    }

    if (preferences.favoriteGenres?.length > 0) {
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      setColor(pdf, SLATE_700);
      pdf.text('Preferred Genres:', 20, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(preferences.favoriteGenres.join(', '), 55, y);
      y += 5;
    }

    if (preferences.favoriteTropes) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Loves in a Book:', 20, y);
      pdf.setFont('helvetica', 'normal');
      const tropeLines = pdf.splitTextToSize(preferences.favoriteTropes, pageWidth - 75);
      pdf.text(tropeLines, 55, y);
      y += tropeLines.length * 4 + 4;
    }
  }

  // ═══════════════════════════════════════
  // FOOTER on all pages
  // ═══════════════════════════════════════
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    drawFooter(pdf, verificationCode, i, totalPages);
  }

  return pdf;
}

/**
 * Generate a simple verification code from readerId
 */
export function generateVerificationCode(readerId) {
  if (!readerId) return '';
  const hash = readerId.split('').reduce((acc, c) => ((acc << 5) - acc + c.charCodeAt(0)) | 0, 0);
  const code = Math.abs(hash).toString(36).toUpperCase().padStart(8, '0').slice(0, 8);
  return `NR-${code}`;
}
